<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Banner from 'dashboard/components-next/banner/Banner.vue';
import AiProviderStatusAPI from 'dashboard/api/aiProviderStatus';
import { LocalStorage } from 'shared/helpers/localStorage';

const props = defineProps({
  accountId: {
    type: [Number, String],
    default: null,
  },
});

const { t } = useI18n();
const POLL_INTERVAL = 30_000;
const STORAGE_PREFIX = 'dismissedLumeraiExhaustionBanner';
const status = ref(null);
const dismissedKey = ref(null);
let pollTimer;

const quota = computed(() => status.value?.lumerai_quota || {});
const currentDismissKey = computed(() =>
  quota.value.exhausted
    ? `${props.accountId}:${quota.value.consumed}:${quota.value.total}`
    : null
);
const storageKey = computed(() => `${STORAGE_PREFIX}:${props.accountId}`);
const shouldShow = computed(
  () => quota.value.exhausted && dismissedKey.value !== currentDismissKey.value
);
const bannerMessage = computed(() =>
  t('CONVERSATION.AI_PROVIDER_STATUS.QUOTA_EXHAUSTED', {
    consumed: quota.value.consumed,
    total: quota.value.total,
  })
);

const loadDismissal = () => {
  dismissedKey.value = LocalStorage.get(storageKey.value);
};

const dismiss = () => {
  dismissedKey.value = currentDismissKey.value;
  LocalStorage.set(storageKey.value, dismissedKey.value);
};

const fetchStatus = async () => {
  if (!props.accountId || document.hidden) return;

  try {
    const { data } = await AiProviderStatusAPI.get();
    status.value = data;
    if (!data?.lumerai_quota?.exhausted) {
      dismissedKey.value = null;
      LocalStorage.remove(storageKey.value);
    }
  } catch (error) {
    status.value = null;
  }
};

const handleVisibilityChange = () => {
  if (!document.hidden) fetchStatus();
};

const startPolling = () => {
  clearInterval(pollTimer);
  status.value = null;
  if (!props.accountId) return;

  loadDismissal();
  fetchStatus();
  pollTimer = setInterval(fetchStatus, POLL_INTERVAL);
};

watch(() => props.accountId, startPolling);

onMounted(() => {
  startPolling();
  document.addEventListener('visibilitychange', handleVisibilityChange);
});

onBeforeUnmount(() => {
  clearInterval(pollTimer);
  document.removeEventListener('visibilitychange', handleVisibilityChange);
});
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <Banner
    v-if="shouldShow"
    color="ruby"
    :action-label="t('CONVERSATION.AI_PROVIDER_STATUS.DISMISS')"
    class="!rounded-none !justify-center [&_p]:m-0"
    role="alert"
    aria-live="assertive"
    @action="dismiss"
  >
    <p class="text-xs font-medium text-center">{{ bannerMessage }}</p>
  </Banner>
</template>
